import {
  Actions,
  Alert,
  Box,
  ButtonLink,
  ContentBlock,
  Heading,
  IconArrow,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import React from 'react';

import type { PublishedListing } from '#shared/types';
import { fetchListings } from '#src/api/communityDirectory';
import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';

import { DirectoryFilters } from './DirectoryFilters';
import { ListingResults } from './ListingResults';
import {
  activeListings,
  filterListings,
  groupListingsByCategory,
  sortListings,
  uniqueServiceTypes,
  type ListingSort,
} from './filterListings';

export const CommunityDirectory = () => {
  const [listings, setListings] = React.useState<PublishedListing[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [serviceType, setServiceType] = React.useState('All');
  const [sort, setSort] = React.useState<ListingSort>('alphabetical');
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    fetchListings()
      .then((snapshot) => setListings(activeListings(snapshot.listings)))
      .catch(() => setError('Could not load listings. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  const availableServiceTypes = uniqueServiceTypes(listings);
  const selectedServiceType =
    serviceType !== 'All' && !availableServiceTypes.includes(serviceType)
      ? 'All'
      : serviceType;
  const filtered = filterListings(listings, selectedServiceType, search);
  const sorted = sortListings(
    filtered,
    sort === 'datePosted' ? 'datePosted' : 'alphabetical',
  );
  const groups =
    sort === 'category' ? groupListingsByCategory(filtered) : undefined;

  return (
    <PageWrapper>
      <ContentBlock>
        <Stack space="xlarge">
          <Stack space="medium">
            <Heading level="1">Community Directory</Heading>
            <Text>
              A directory of local businesses, skills and services offered by
              members of the Amsleigh Park community. Know someone who should be
              listed?{' '}
              <TextLink href="/community-directory/submit">
                Submit a listing
              </TextLink>
              .
            </Text>
          </Stack>

          <Alert tone="info">
            <Text>
              Listings are provided free by community members. Amsleigh Park PS
              and the PFA take no responsibility for these services, and there
              is no expectation of discounted or free work.
            </Text>
          </Alert>

          <DirectoryFilters
            availableServiceTypes={availableServiceTypes}
            serviceType={selectedServiceType}
            sort={sort}
            search={search}
            onServiceTypeChange={setServiceType}
            onSortChange={setSort}
            onSearchChange={setSearch}
          />

          <ListingResults
            listings={sorted}
            groups={groups}
            loading={loading}
            error={error}
          />

          <Box paddingTop="medium">
            <Actions>
              <ButtonLink
                href="/community-directory/submit"
                tone="formAccent"
                icon={<IconArrow direction="right" />}
                iconPosition="trailing"
              >
                Submit a listing
              </ButtonLink>
            </Actions>
          </Box>
        </Stack>
      </ContentBlock>
    </PageWrapper>
  );
};
